import { cva } from '@styled-system/css';
import { token } from '@styled-system/tokens';
import type { SystemProperties } from '@styled-system/types';
import { type ButtonHTMLAttributes, type ReactNode, isValidElement, useState } from 'react';
import { PuffLoader } from 'react-spinners';

import { logoVariants } from './Logo/variants';

import { Logo, type LogoVariant } from './Logo';

/** Either a legacy LogoVariant string or any ReactNode (e.g. a Lucide icon). */
type IconProp = LogoVariant | ReactNode;

const isLogoVariant = (value: unknown): value is LogoVariant =>
  typeof value === 'string' && (logoVariants as readonly string[]).includes(value);

const renderIcon = (icon: IconProp, size: number, color?: string) => {
  if (icon == null || icon === false) return null;
  if (isLogoVariant(icon)) return <Logo variant={icon} size={size} color={color} />;
  if (isValidElement(icon)) return icon;
  return null;
};

export type ButtonVariant = 'primary' | 'cta' | 'secondary' | 'ghost' | 'danger' | 'success' | 'warning';
export type ButtonSize = 'sm' | 'md' | 'lg';

const buttonRecipe = cva({
  base: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '2',
    fontFamily: 'sans',
    fontWeight: 'medium',
    whiteSpace: 'nowrap',
    cursor: 'pointer',
    transition: 'background 120ms ease, color 120ms ease, border-color 120ms ease, box-shadow 120ms ease',
    _focusVisible: {
      outline: '2px solid token(colors.border.focus)',
      outlineOffset: '1px',
    },
    _disabled: {
      cursor: 'not-allowed',
      opacity: 0.55,
    },
    // Désactivé SANS l'attribut natif (`aria-disabled`, ex. action verrouillée par le plan qui
    // garde survol + infobulle) : même rendu que `_disabled` ; le survol de chaque variante est
    // neutralisé dans la variante (sélecteur plus spécifique que `_hover`, boutons normaux intacts).
    '&[aria-disabled=true]': {
      cursor: 'not-allowed',
      opacity: 0.55,
    },
  },
  variants: {
    variant: {
      primary: {
        bg: 'brand.primary',
        color: 'white',
        borderWidth: '1px',
        borderStyle: 'solid',
        borderColor: 'brand.primary',
        boxShadow: 'primary',
        _hover: { bg: 'brand.primaryHover', borderColor: 'brand.primaryHover' },
        '&[aria-disabled=true]': {
          _hover: { bg: 'brand.primary', borderColor: 'brand.primary' },
          _active: { bg: 'brand.primary', borderColor: 'brand.primary' },
        },
      },
      cta: {
        bg: 'cta.default',
        color: 'cta.foreground',
        borderWidth: '1px',
        borderStyle: 'solid',
        borderColor: 'cta.default',
        _hover: { bg: 'cta.hover', borderColor: 'cta.hover' },
        '&[aria-disabled=true]': {
          _hover: { bg: 'cta.default', borderColor: 'cta.default' },
          _active: { bg: 'cta.default', borderColor: 'cta.default' },
        },
      },
      secondary: {
        bg: 'surface.default',
        color: 'text.primary',
        borderWidth: '1px',
        borderStyle: 'solid',
        borderColor: 'border.strong',
        boxShadow: 'xs',
        _hover: { bg: 'surface.subtle' },
        '&[aria-disabled=true]': { _hover: { bg: 'surface.default' }, _active: { bg: 'surface.default' } },
      },
      ghost: {
        bg: 'transparent',
        color: 'text.secondary',
        borderWidth: '1px',
        borderStyle: 'solid',
        borderColor: 'transparent',
        _hover: { bg: 'surface.sunken', color: 'text.primary' },
        '&[aria-disabled=true]': {
          _hover: { bg: 'transparent', color: 'text.secondary' },
          _active: { bg: 'transparent', color: 'text.secondary' },
        },
      },
      danger: {
        bg: 'error.default',
        color: 'white',
        borderWidth: '1px',
        borderStyle: 'solid',
        borderColor: 'error.default',
        _hover: { filter: 'brightness(0.95)' },
        '&[aria-disabled=true]': { _hover: { filter: 'none' }, _active: { filter: 'none' } },
      },
      success: {
        bg: 'success.default',
        color: 'white',
        borderWidth: '1px',
        borderStyle: 'solid',
        borderColor: 'success.default',
        _hover: { filter: 'brightness(0.95)' },
        '&[aria-disabled=true]': { _hover: { filter: 'none' }, _active: { filter: 'none' } },
      },
      warning: {
        // Outline orange (même style que le StatusChip warning) : fond à peine
        // teinté, bordure + texte orange.
        bg: 'warning.soft',
        color: 'warning.default',
        borderWidth: '1px',
        borderStyle: 'solid',
        borderColor: 'warning.border',
        _hover: { filter: 'brightness(1.08)' },
        '&[aria-disabled=true]': { _hover: { filter: 'none' }, _active: { filter: 'none' } },
      },
    },
    size: {
      sm: {
        h: '6',
        minH: '26px',
        px: '5',
        fontSize: 'caption',
        borderRadius: 'lg',
      },
      md: {
        h: '8',
        minH: '32px',
        px: '6',
        fontSize: 'bodySm',
        borderRadius: 'lg',
      },
      lg: {
        h: '10',
        minH: '40px',
        px: '8',
        fontSize: 'body',
        borderRadius: 'lg',
      },
    },
  },
  defaultVariants: {
    variant: 'secondary',
    size: 'md',
  },
});

export interface ButtonProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'color' | 'onClick'> {
  children?: ReactNode;
  handleClick?: (e: React.MouseEvent<HTMLButtonElement>) => void | Promise<void>;
  onClick?: (e: React.MouseEvent<HTMLButtonElement>) => void | Promise<void>;
  variant?: ButtonVariant;
  size?: ButtonSize;
  /**
   * Leading icon. Accepts either a legacy `LogoVariant` string
   * (e.g. `"plus"`) or any ReactNode — typically a Lucide icon
   * such as `<Plus size={14} />`.
   */
  icon?: IconProp;
  /** Trailing icon — same shape as `icon`. */
  trailingIcon?: IconProp;
  showLoader?: boolean;
  /** Force a custom text/icon color. Accepts a Panda token path (e.g. 'text.primary') or any CSS color. */
  color?: SystemProperties['color'];
  className?: string;
}

/**
 * DS Button — 6 variants × 3 sizes.
 *
 * The legacy API (`handleClick`, `icon` as LogoVariant, `color`, `showLoader`)
 * is preserved so existing call sites keep working during the AntD coexistence
 * period. The `variant` and `size` props default to `secondary` / `md` to
 * approximate the previous look.
 */
export const Button = ({
  children,
  handleClick,
  onClick,
  disabled: disabledProp,
  variant = 'secondary',
  size = 'md',
  icon,
  trailingIcon,
  showLoader = false,
  color,
  className,
  type = 'button',
  ...rest
}: ButtonProps) => {
  const [isLoading, setLoading] = useState(false);
  const disabled = (disabledProp ?? false) || isLoading;

  const resolvedColor =
    color && typeof color === 'string' && color.includes('.')
      ? token.var(`colors.${color}` as Parameters<typeof token.var>[0])
      : (color as string | undefined);

  const handleClickEvent = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    const callback = handleClick ?? onClick;
    if (!callback) return;
    (async () => {
      setLoading(true);
      try {
        await callback(e);
      } finally {
        setLoading(false);
      }
    })().catch(console.error);
  };

  const iconSize = size === 'sm' ? 12 : size === 'lg' ? 16 : 14;
  const recipeClass = buttonRecipe({ variant, size });

  return (
    <button
      {...rest}
      type={type}
      className={`${recipeClass} ${className ?? ''}`}
      onClick={handleClickEvent}
      disabled={disabled}
      style={resolvedColor ? { color: resolvedColor, ...rest.style } : rest.style}
    >
      {renderIcon(icon, iconSize, resolvedColor)}
      {children}
      {renderIcon(trailingIcon, iconSize, resolvedColor)}
      {isLoading && showLoader && (
        <PuffLoader size={iconSize} color={resolvedColor ?? token.var('colors.text.primary')} />
      )}
    </button>
  );
};
